import { apiJson } from '../api';

/**
 * An email digest of one saved search. `description_id` is the saved search
 * (`SavedSearch.id`) the digest reads; `articles_sent` counts the send ledger,
 * which is what guarantees a story is never emailed to the same subscriber
 * twice.
 */
export interface Digest {
  id: string;
  user_id: string;
  description_id: string;
  cadence: string;
  article_count: number;
  active: boolean;
  last_claimed_at?: string | null;
  last_sent_at?: string | null;
  created_at: string;
  updated_at: string;
  search_name?: string;
  search_description?: string;
  articles_sent?: number;
}

/** One option in the cadence picker. Served by the backend so the frontend
 *  cannot offer a cadence the sender does not know how to schedule, and so the
 *  length each cadence implies is stated in one place. */
export interface DigestCadenceOption {
  value: string;
  label: string;
  article_count: number;
}

/** No length here on purpose: it follows from the cadence, server-side. */
export interface DigestCreatePayload {
  description_id: string;
  cadence: string;
}

export interface DigestUpdatePayload {
  cadence: string;
}

export async function listDigests(): Promise<Digest[]> {
  const data = await apiJson<Digest[]>('/api/digests');
  return Array.isArray(data) ? data : [];
}

export async function listDigestCadences(): Promise<DigestCadenceOption[]> {
  const data = await apiJson<DigestCadenceOption[]>('/api/digest-cadences');
  return Array.isArray(data) ? data : [];
}

export async function createDigest(payload: DigestCreatePayload): Promise<Digest> {
  return apiJson<Digest>('/api/digests', { method: 'POST', json: payload });
}

export async function updateDigest(id: string, payload: DigestUpdatePayload): Promise<Digest> {
  return apiJson<Digest>(`/api/digests/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json: payload,
  });
}

export async function deleteDigest(id: string): Promise<void> {
  await apiJson<void>(`/api/digests/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
